import React, { useState } from 'react';
import { TextField, MenuItem, IconButton, Box } from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Field } from './edit-object/design/Field';


interface StarredSelectProps {
  label: string;
  options: readonly string[];
  value?: string;
  onChange?: (value: string) => void;
  /**
   * 'mui' (default) renders the legacy MUI select with a notched outline
   * label and per-option star toggles. 'design' renders a native Field +
   * Select pair so the label matches the rest of the edit-object hi-fi
   * panel — note: the per-option star-toggle UI is dropped in this
   * variant (native <select> can't host icon buttons).
   */
  variant?: 'mui' | 'design';
}

export const StarredSelect = ({ label, options, value = '', onChange, variant = 'mui' }: StarredSelectProps) => {
  const theme = useTheme();
  const [starred, setStarred] = useState<Set<string>>(new Set());

  const toggleStar = (option: string) => {
    const newStarred = new Set(starred);
    if (starred.has(option)) {
      newStarred.delete(option);
    } else {
      newStarred.add(option);
    }
    setStarred(newStarred);
  };

  const sortedOptions = [...options].sort((a, b) => {
    const aStarred = starred.has(a);
    const bStarred = starred.has(b);
    return aStarred === bStarred ? a.localeCompare(b) : bStarred ? 1 : -1;
  });

  if (variant === 'design') {
    return (
      <Field label={label}>
        <TextField
          select
          fullWidth
          variant="standard"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          slotProps={{
            input: { disableUnderline: true },
            inputLabel: { shrink: false },
            htmlInput: { 'aria-label': label },
          }}
        >
          {sortedOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Field>
    );
  }

  return (
    <TextField
      select
      fullWidth
      label={label}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      variant="outlined"
    >
      {sortedOptions.map((option) => (
        <MenuItem key={option} value={option}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <span>{option}</span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleStar(option);
              }}
            >
              {starred.has(option) ? (
                <Star sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
              ) : (
                <StarBorder sx={{ color: theme.palette.text.disabled, fontSize: 16 }} />
              )}
            </IconButton>
          </Box>
        </MenuItem>
      ))}
    </TextField>
  );
};
